import api from './api';
import { ApiResponse, User, Plan, Subscription } from '../types';

export interface AiUsageMonth {
  month: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  value_cents: number;
  charged_cents: number;
}

export interface AiCreditsStatement {
  account: { balance_cents: number; postpaid: boolean; notes?: string | null };
  billing_start: string;
  billing_active: boolean;
  months: AiUsageMonth[];
  features: Array<{ month: string; feature: string; calls: number; tokens: number; value_cents: number }>;
  transactions?: Array<{ id: string; type: string; amount_cents: number; balance_after_cents?: number | null; description?: string | null; created_at: string }>;
  // Franquia do mês, médias por ação e automações que mais gastam (03/11/2026)
  summary?: import('./aiCreditsService').AiCreditSummary;
  averages?: import('./aiCreditsService').AiActionAverage[];
  top_automations?: Array<{ workflow_id: string; name: string | null; runs: number; total_cents: number; tokens: number; avg_cents_per_run: number }>;
}

export interface AiMonthlyClosing {
  month: string;
  billing_start: string;
  total_cents: number;
  accounts: Array<{
    user_id: string | null;
    email: string | null;
    name: string | null;
    company: string | null;
    calls: number;
    input_tokens: number;
    output_tokens: number;
    value_cents: number;
    charged_cents: number;
    balance_cents: number | null;
    postpaid: boolean;
  }>;
}

interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  plan_id?: string;
  has_subscription?: string;
  created_from?: string;
  created_to?: string;
}

interface GetSubscriptionsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  plan_id?: string;
  billing_cycle?: string;
  created_from?: string;
  created_to?: string;
  manual_only?: string;
}

interface DashboardStats {
  totalUsers: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  totalContent: number;
  userGrowth: number;
  revenueGrowth: number;
  churnRate: number;
  avgRevenuePerUser: number;
}

interface DashboardCharts {
  revenueHistory: { month: string; revenue: number }[];
  userGrowthHistory: { month: string; users: number }[];
  planDistribution: { name: string; value: number }[];
  recentActivity: { id: string; type: string; description: string; created_at: string }[];
}

export const adminService = {
  // Video Studio pricing
  getVideoPricing: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/admin/video-pricing');
    return response.data;
  },

  updateVideoPricing: async (config: any): Promise<ApiResponse<any>> => {
    const response = await api.put('/admin/video-pricing', { config });
    return response.data;
  },

  resetVideoPricing: async (): Promise<ApiResponse<any>> => {
    const response = await api.post('/admin/video-pricing/reset');
    return response.data;
  },

  // Dashboard
  getDashboardStats: async (): Promise<ApiResponse<DashboardStats>> => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },

  getDashboardCharts: async (): Promise<ApiResponse<DashboardCharts>> => {
    const response = await api.get('/admin/dashboard/charts');
    return response.data;
  },

  // Users
  getUsers: async (params: GetUsersParams = {}): Promise<ApiResponse<User[]>> => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  getUser: async (id: string): Promise<ApiResponse<User>> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  updateUserStatus: async (id: string, status: string): Promise<ApiResponse<User>> => {
    const response = await api.patch(`/admin/users/${id}/status`, { status });
    return response.data;
  },

  updateUserRole: async (id: string, role: string): Promise<ApiResponse<User>> => {
    const response = await api.patch(`/admin/users/${id}/role`, { role });
    return response.data;
  },

  deleteUser: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  impersonateUser: async (id: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    const response = await api.post(`/admin/users/${id}/impersonate`);
    return response.data;
  },

  sendUserEmail: async (id: string, data: { subject: string; template: string }): Promise<ApiResponse<void>> => {
    const response = await api.post(`/admin/users/${id}/email`, data);
    return response.data;
  },

  // Plans
  getPlans: async (): Promise<ApiResponse<Plan[]>> => {
    const response = await api.get('/admin/plans');
    return response.data;
  },

  getPlan: async (id: string): Promise<ApiResponse<Plan>> => {
    const response = await api.get(`/admin/plans/${id}`);
    return response.data;
  },

  createPlan: async (data: Partial<Plan>): Promise<ApiResponse<Plan>> => {
    const response = await api.post('/admin/plans', data);
    return response.data;
  },

  updatePlan: async (id: string, data: Partial<Plan>): Promise<ApiResponse<Plan>> => {
    const response = await api.put(`/admin/plans/${id}`, data);
    return response.data;
  },

  deletePlan: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/admin/plans/${id}`);
    return response.data;
  },

  // Subscriptions
  getSubscriptions: async (params: GetSubscriptionsParams = {}): Promise<ApiResponse<Subscription[]>> => {
    const response = await api.get('/admin/subscriptions', { params });
    return response.data;
  },

  getSubscription: async (id: string): Promise<ApiResponse<Subscription>> => {
    const response = await api.get(`/admin/subscriptions/${id}`);
    return response.data;
  },

  cancelSubscription: async (id: string): Promise<ApiResponse<Subscription>> => {
    const response = await api.post(`/admin/subscriptions/${id}/cancel`);
    return response.data;
  },

  refundSubscription: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.post(`/admin/subscriptions/${id}/refund`);
    return response.data;
  },

  extendSubscription: async (id: string, data: { extend_days?: number; extend_to?: string; reason?: string }): Promise<ApiResponse<Subscription>> => {
    const response = await api.post(`/admin/subscriptions/${id}/extend`, data);
    return response.data;
  },

  reactivateSubscription: async (id: string, data?: { reason?: string }): Promise<ApiResponse<Subscription>> => {
    const response = await api.post(`/admin/subscriptions/${id}/reactivate`, data || {});
    return response.data;
  },

  changeSubscriptionPlan: async (id: string, data: {
    plan_id: string;
    billing_cycle?: 'monthly' | 'yearly';
    is_free?: boolean;
    custom_price?: number;
    expires_at?: string;
    notes?: string;
    custom_limits?: Record<string, number>;
  }): Promise<ApiResponse<Subscription>> => {
    const response = await api.post(`/admin/subscriptions/${id}/change-plan`, data);
    return response.data;
  },

  // Audit Logs
  getAuditLogs: async (params: { page?: number; limit?: number; user_id?: string }): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/admin/audit-logs', { params });
    return response.data;
  },

  // Settings
  getSettings: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  updateSettings: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.put('/admin/settings', data);
    return response.data;
  },

  // Companies
  getCompanies: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    plan_id?: string;
    has_subscription?: string;
  } = {}): Promise<ApiResponse<User[]>> => {
    const response = await api.get('/admin/companies', { params });
    return response.data;
  },

  assignPlanToUser: async (data: {
    user_id: string;
    plan_id: string;
    billing_cycle?: string;
    is_free?: boolean;
    custom_price?: number;
    expires_at?: string;
    notes?: string;
    custom_limits?: Record<string, number>;
  }): Promise<ApiResponse<any>> => {
    const response = await api.post('/admin/companies/assign-plan', data);
    return response.data;
  },

  generatePaymentLink: async (data: {
    user_id: string;
    plan_id: string;
    billing_cycle?: string;
    currency?: string;
    custom_price?: number;
  }): Promise<ApiResponse<any>> => {
    const response = await api.post('/admin/companies/payment-link', data);
    return response.data;
  },

  // Video credits — admin manual grant
  getCompanyVideoCredits: async (userId: string): Promise<ApiResponse<{
    user: { id: string; email: string; first_name?: string; last_name?: string; company_name?: string };
    balance: { total: number; used: number; available: number; monthly_free: number; monthly_reset_at?: string };
    transactions: Array<{
      id: string;
      type: 'debit' | 'credit' | 'refund' | 'grant';
      amount: number;
      source: string;
      description?: string | null;
      balance_after?: number | null;
      created_at: string;
    }>;
  }>> => {
    const response = await api.get(`/admin/companies/${userId}/video-credits`);
    return response.data;
  },

  grantVideoCredits: async (data: {
    user_id: string;
    amount: number;
    notes?: string;
  }): Promise<ApiResponse<{
    balance: { total: number; used: number; available: number; monthly_free: number; monthly_reset_at?: string };
  }>> => {
    const response = await api.post('/admin/companies/grant-video-credits', data);
    return response.data;
  },

  // Créditos de IA (valores em centavos de real)
  getAiCredits: async (userId: string): Promise<ApiResponse<AiCreditsStatement>> => {
    const response = await api.get(`/admin/ai-credits/${userId}`);
    return response.data;
  },

  // Limite a mais: franquia mensal própria da conta (null volta para a do plano)
  setAiMonthlyLimit: async (userId: string, data: { monthly_cents: number | null; notes?: string }): Promise<ApiResponse<{ included_monthly_cents: number; included_remaining_cents: number }>> => {
    const response = await api.put(`/admin/ai-credits/${userId}/limit`, data);
    return response.data;
  },

  addAiCredits: async (userId: string, data: { amount_cents: number; notes?: string }): Promise<ApiResponse<{ balance_cents: number; postpaid: boolean }>> => {
    const response = await api.post(`/admin/ai-credits/${userId}/credits`, data);
    return response.data;
  },

  setAiPostpaid: async (userId: string, data: { postpaid: boolean; notes?: string }): Promise<ApiResponse<{ balance_cents: number; postpaid: boolean }>> => {
    const response = await api.put(`/admin/ai-credits/${userId}/postpaid`, data);
    return response.data;
  },

  getAiClosing: async (month: string): Promise<ApiResponse<AiMonthlyClosing>> => {
    const response = await api.get('/admin/ai-credits/closing', { params: { month } });
    return response.data;
  },

  // Stripe Sync
  syncPlansWithStripe: async (): Promise<ApiResponse<any>> => {
    const response = await api.post('/admin/plans/sync-stripe');
    return response.data;
  },

  syncPlanWithStripe: async (id: string): Promise<ApiResponse<Plan>> => {
    const response = await api.post(`/admin/plans/${id}/sync-stripe`);
    return response.data;
  }
};

export default adminService;
